import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {FireInTheHole2} from "../FireInTheHole2";
import {FITH2Symbol, ISymbolData} from "../symbols/FITH2Symbol";
import {FITH2Model} from "../server/FITH2Model";
import {FITH2AvalancheReelModel} from "./FITH2AvalancheReelModel";
import {FITH2GameConfig} from "../configs/FITH2GameConfig";
import {gsap} from "gsap";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {FITH2SoundManager} from "../sound/FITH2SoundManager";

export class FITH2ReelArea extends PIXI.Container implements StageLayer {
    public readonly DROP_IN_REEL_DELAY: number = 0.02;
    private readonly FAST_DROP_IN_REEL_DELAY: number = 0;
    private readonly FULL_UP_REEL_DELAY: number = 0.05;
    private readonly DROP_OUT_REEL_DELAY: number = 0.02;
    private readonly FILL_UP_REEL_DELAY: number = 0.05;
    private readonly COLLAPSE_REEL_DELAY: number = 0.05;
    private readonly DROP_IN_NEAR_WIN_DELAY: number = 1.5;
    private readonly FAST_DROP_IN_NEAR_WIN_DELAY: number = 0.3;
    private _allReels: FITH2AvalancheReelModel[] = [];
    private _needFillUpReels: boolean[];

    get isFastSpin(): boolean {
        if (FireInTheHole2.apiPlugin.settings.get(APISetting.FAST_SPIN, false)) {
            return true;
        }

        if (FireInTheHole2.model.data.wasFeatureBuy && (FireInTheHole2.model.isNormal && !FireInTheHole2.model.isLuckyDraw)) {
            return true;
        }

        return false;
    }

    get hasAnticipation(): boolean {
        if (FireInTheHole2.apiPlugin.settings.get(APISetting.FAST_SPIN, false)) {
            return false;
        }

        return true;
    }

    constructor() {
        super();
        this.init();
    }

    public init(): void {
        if (this._allReels.length > 0) {
            return;
        }
        this.name = "ReelAreaView";
        this._allReels = [];

        const data: FITH2Model = FireInTheHole2.model,
            reelsData: ISymbolData[][] = data.reelsData;
        let tempReel: ISymbolData[];

        for (let i: number = 0; i < reelsData.length; i++) {
            tempReel = [];
            const reelModel: FITH2AvalancheReelModel = new FITH2AvalancheReelModel(i);
            this._allReels.push(reelModel);
            this.addChild(reelModel.reelView);
            reelModel.init(reelsData[i]);
        }

        for (let j: number = 0; j < this._allReels.length; j++) {
            FireInTheHole2.highSymbolRealArea.addChild(this._allReels[j].reelView.dirtSymbolView);
            FireInTheHole2.highSymbolRealArea.addChild(this._allReels[j].reelView.scatterSymbolView);
            FireInTheHole2.symbolAnimation.addChild(this._allReels[j].reelView.initialWinSymbolView);
            FireInTheHole2.wildWinAnimation.addChild(this._allReels[j].reelView.fullWildSymbolWinView);
            FireInTheHole2.symbolAnimation.addChild(this._allReels[j].reelView.scatterWinView);
        }

        const mainReelsMask: PIXI.Graphics = new PIXI.Graphics();
        mainReelsMask.name = "reelAreaMask";
        mainReelsMask.beginFill(0xFFFF0B, 1);
        mainReelsMask.drawRect(-FITH2GameConfig.symbolWidth, 0, this.getReelAreaWidth() + FITH2GameConfig.symbolWidth, this.getReelAreaHeight());

        this.addChild(mainReelsMask);
        this.mask = mainReelsMask;
        this.position = FITH2GameConfig.reelAreaPos;
    }
    public reInit(): void {
        const reelsData: ISymbolData[][] = FireInTheHole2.model.reelsData;
        FireInTheHole2.freespinIntro.isFromAS = true;
        for (let i: number = 0; i < reelsData.length; i++) {
            this._allReels[i].reelView.dropOut(0).progress(1);
            this._allReels[i].reelView.dropIn(0, false).progress(1);
        }
    }

    private getReelAreaWidth(): number {
        return (FITH2GameConfig.symbolWidth * FITH2GameConfig.reelsNum) + 20;
    }

    private getReelAreaHeight(): number {
        return FITH2GameConfig.symbolHeight * 6;
    }

    public get needFillUpReels(): boolean[] {
        return this._needFillUpReels;
    }

    public dropIn(jurisdictionDelay: number): gsap.core.Timeline[] {
        const animations: gsap.core.Timeline[] = [],
            nearWinReels: boolean[] = FireInTheHole2.model.nearWinReels,
            hasAnticipation: boolean = this.hasAnticipation,
            nearWinDelay: number = this.hasAnticipation ? this.DROP_IN_NEAR_WIN_DELAY : this.FAST_DROP_IN_NEAR_WIN_DELAY,
            dropInDelay: number = this.isFastSpin ? this.FAST_DROP_IN_REEL_DELAY : this.DROP_IN_REEL_DELAY,
            padding: number = MathHelper.roundToDecimals(jurisdictionDelay / 5, 1);
        this._needFillUpReels = [true, true, true, true, true, true];
        FITH2SoundManager.newAvalanche = true;
        let delay: number = 0;
        this._allReels.forEach((reel: FITH2AvalancheReelModel, i: number) => {
            delay += (nearWinReels[i] ? nearWinDelay : 0);
            animations.push(reel.reelView.dropIn(delay + padding * i, hasAnticipation));
            delay += dropInDelay;
        });
        return animations;
    }

    public dropOut(): gsap.core.Timeline {
        const animations = gsap.timeline();
        this._allReels.forEach((reel: FITH2AvalancheReelModel, i: number) => {
            animations.add(reel.reelView.dropOut(Math.abs(2.5 - i) * this.DROP_OUT_REEL_DELAY), 0);
        });
        return animations;
    }

    public fillUp(): gsap.core.Timeline[] {
        const animations: gsap.core.Timeline[] = [],
            nearWinReels: boolean[] = FireInTheHole2.model.nearWinReels,
            hasAnticipation: boolean = this.hasAnticipation;
        let delay: number = 0;
        FITH2SoundManager.newAvalanche = true;
        this._needFillUpReels = [true, true, true, true, true, true];
        this._allReels.forEach((reel: FITH2AvalancheReelModel, i: number) => {
            if (reel.reelView.needFillUp) {
                delay += (i * this.FULL_UP_REEL_DELAY + ((nearWinReels[i] && i > 0) ? this.DROP_IN_NEAR_WIN_DELAY : 0));
                animations.push(reel.reelView.fillUp(delay, hasAnticipation));
            } else {
                this._needFillUpReels[i] = false;
            }
        });
        return animations;
    }

    public collapse(): gsap.core.Timeline[] {
        const animations: gsap.core.Timeline[] = [], barId: number = FireInTheHole2.model.barId;
        let delay: number = 0;
        this._allReels.forEach((reel: FITH2AvalancheReelModel) => {
            reel.reelView.calculateCollapseData();
            if (reel.reelView.needCollapse) {
                animations.push(reel.reelView.collapse(delay, barId));
                delay += this.COLLAPSE_REEL_DELAY;
            }
        });
        return animations;
    }

    public updateNewSymbolBehindBar(): void {
        this._allReels.forEach((reel: FITH2AvalancheReelModel) => {
            reel.reelView.updateNewSymbolBehindBar();
        });
    }

    public addDirtFrames(fromExplosion:boolean = false): void {
        this._allReels.forEach((reel: FITH2AvalancheReelModel) => {
            reel.reelView.getAllSymbols().forEach((sym: FITH2Symbol) => {
                    sym.addFrames(false, fromExplosion);
            });
        });
    }

    public switchSymbol(oldSymbol: FITH2Symbol, newSym: string): gsap.core.Timeline {
        return this._allReels[oldSymbol.reelId].reelView.switch(oldSymbol.id, newSym);
    }

    public getSymbol(pos: number[]): FITH2Symbol {
        return this._allReels[pos[0]].reelView.getSymbol(pos[1]);
    }

    get allReels(): FITH2AvalancheReelModel[] {
        return this._allReels;
    }
}